import React from 'react';
import Linkify from 'react-linkify';
import { faMinus } from '@fortawesome/free-solid-svg-icons/faMinus';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';

import Accordion, {
  AccordionContent,
  AccordionTitle
} from '@buyside/components/presentation/Accordion/accordionComponents';
import Link from '@buyside/components/presentation/Link/link';
import MediaShowPageLoading from '@buyside/components/presentation/MediaShowPage/mediaShowPageLoading';
import UserGeneratedLink from '@buyside/components/presentation/UserGeneratedLink/UserGeneratedLink';
import YouTubePlayer from '@buyside/components/presentation/YouTubePlayer/youtubePlayer';

import { formatDate } from '@buyside/util/dateHelper';
import { useIsMobile } from '@buyside/util/hooks/useIsMobile';
import { entityUrl, mediaIndexUrlFor, mediaShowUrlFor } from '@buyside/util/urlHelper';

import { useMediaShowQuery } from '@interfaces/graphql-typings';

import styles from './mediaShowPageStyles.scss';

export enum MediaTypes {
  VIDEO = 'Video',
  PODCAST_EPISODE = 'Podcast Episode'
}

const MediaShowPage: React.FC<{ id: string; mediaType: MediaTypes }> = ({ id, mediaType }) => {
  const isMobile = useIsMobile();

  const { data, loading } = useMediaShowQuery({
    variables: { id, type: mediaType.replace(' ', '') }
  });

  if (loading) return <MediaShowPageLoading mediaType={mediaType} />;

  if (!data) throw new Error();

  const {
    description,
    mediaId,
    mentionedEntities,
    publishedAt,
    relatedMedia,
    tagList,
    title
  } = data.media;

  const isVideo = mediaType === MediaTypes.VIDEO;

  const mediaIndexUrl = mediaIndexUrlFor(mediaType);
  const mediaShowUrl = mediaShowUrlFor(mediaType);

  return (
    <>
      <div className={styles.mediaHeader}>
        <h3>{title}</h3>
        <p>{formatDate(publishedAt)}</p>
      </div>
      <div className={styles.mediaBody}>
        <div>
          {isVideo ? (
            <YouTubePlayer height={isMobile ? 350 : 450} videoId={mediaId} />
          ) : (
            <iframe
              allow="encrypted-media"
              frameBorder="0"
              height={isMobile ? 82 : 152}
              src={`https://open.spotify.com/embed?uri=spotify:episode:${mediaId}`}
              title="Spotify Player"
              width="100%"
            />
          )}
          <p>
            <Linkify
              componentDecorator={(decoratedHref, decoratedText, key): React.ReactElement => (
                <UserGeneratedLink href={decoratedHref} key={key}>
                  {decoratedText}
                </UserGeneratedLink>
              )}
            >
              {description}
            </Linkify>
          </p>
          {mentionedEntities.length ? (
            <div className={styles.mentionedEntities}>
              {mentionedEntities.map(entity => (
                <Link key={`entity-${entity.id}`} to={entityUrl(entity.id)}>
                  {entity.primarySymbol}
                </Link>
              ))}
            </div>
          ) : null}
          {tagList.length ? (
            <div className={styles.tags}>
              {tagList.map((tag, idx) => (
                <Link key={`${tag}-${idx}`} to={mediaIndexUrl(tag)}>
                  #{tag}
                </Link>
              ))}
            </div>
          ) : null}
        </div>
        <Accordion>
          <AccordionTitle collapseIcon={faMinus} expandable expandIcon={faPlus}>
            Related {mediaType}s
          </AccordionTitle>
          <AccordionContent>
            {relatedMedia.length ? (
              relatedMedia.map(media => (
                <div key={`media-${media.id}`} className={styles.relatedMedia}>
                  <Link to={mediaShowUrl(media.vanityLink || media.id)}>{media.title}</Link>
                  <p>{media.description}</p>
                  <div className={styles.tags}>
                    {media.tagList
                      .filter(tag => tagList.includes(tag))
                      .map((tag, idx) => (
                        <Link key={`${media.id}-${tag}-${idx}`} to={mediaIndexUrl(tag)}>
                          #{tag}
                        </Link>
                      ))}
                  </div>
                </div>
              ))
            ) : (
              <p>
                There are no related {mediaType.toLowerCase()}s at the moment. Please check back
                later.
              </p>
            )}
          </AccordionContent>
        </Accordion>
      </div>
    </>
  );
};

export default MediaShowPage;
